(function () {
  const IN_TO_CM = 2.54;

  function formatRange(range, unit) {
    if (!range || range.length < 2) return '';
    const min = range[0];
    const max = range[1];
    if (unit === 'cm') {
      return Math.ceil(min * IN_TO_CM) + '-' + Math.ceil(max * IN_TO_CM);
    }
    return min + '-' + max;
  }

  function parsePayload(raw) {
    try {
      return JSON.parse(raw) || {};
    } catch (e) {
      return {};
    }
  }

  function buildMeta(inseam, fit) {
    const parts = [];
    if (inseam) parts.push('INSEAM: ' + String(inseam).toUpperCase());
    if (fit) parts.push('FIT: ' + String(fit).toUpperCase());
    return parts.join('\u00A0\u00A0');
  }

  class SizeGuideDrawer extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;

      this.overlay = this.querySelector('[data-size-guide-overlay]');
      this.countrySelect = this.querySelector('[data-size-guide-country]');
      this.unitButtons = this.querySelectorAll('[data-size-guide-unit]');
      this.head = this.querySelector('[data-size-guide-head]');
      this.body = this.querySelector('[data-size-guide-rows]');
      this.titleEl = this.querySelector('[data-size-guide-title]');
      this.metaEl = this.querySelector('[data-size-guide-meta]');
      this.howtoEl = this.querySelector('[data-size-guide-howto]');
      this.unit = 'in';

      const chartsNode =
        this.querySelector('[data-size-guide-charts]') || document.getElementById('SizeGuideCharts');
      this.charts = parsePayload(chartsNode ? chartsNode.textContent : '{}');

      const defaultNode = this.querySelector('[data-size-guide-json]');
      this.defaultPayload = parsePayload(defaultNode ? defaultNode.textContent : '{}');
      this.applyPayload(this.defaultPayload, false);

      this.querySelectorAll('[data-size-guide-close]').forEach((btn) => {
        btn.addEventListener('click', () => this.close());
      });
      if (this.overlay) this.overlay.addEventListener('click', () => this.close());

      if (this.countrySelect) {
        this.countrySelect.addEventListener('change', () => {
          this.countryId = this.countrySelect.value;
          this.render();
        });
      }

      this.unitButtons.forEach((btn) => {
        btn.addEventListener('click', () => {
          this.unit = btn.getAttribute('data-size-guide-unit') || 'in';
          this.unitButtons.forEach((b) => b.classList.toggle('is-active', b === btn));
          this.render();
        });
      });

      // Capture phase so Escape closes only the size guide when it is open over cart.
      document.addEventListener(
        'keydown',
        (event) => {
          if (event.key !== 'Escape' || !this.classList.contains('active')) return;
          event.preventDefault();
          event.stopPropagation();
          this._suppressEscape = true;
          this.close();
        },
        true
      );
      document.addEventListener(
        'keyup',
        (event) => {
          if (event.key !== 'Escape' || !this._suppressEscape) return;
          event.preventDefault();
          event.stopPropagation();
          this._suppressEscape = false;
        },
        true
      );

      document.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-size-guide-drawer-open]');
        if (!trigger) return;
        event.preventDefault();
        event.stopPropagation();
        this.openFromTrigger(trigger);
      });
    }

    openFromTrigger(trigger) {
      const payloadId = trigger.getAttribute('data-size-guide-payload');
      if (payloadId) {
        const node = document.getElementById(payloadId);
        if (node) {
          this.applyPayload(parsePayload(node.textContent), true);
          this.open();
          return;
        }
      }
      this.applyPayload(this.defaultPayload, true);
      this.open();
    }

    resolveChartEntry(payload) {
      const data = payload || {};
      // Prefer shared registry by chartId; allow legacy inline chart during transition.
      if (data.chart) {
        return { chart: data.chart, guideHtml: data.guideHtml || '' };
      }
      const chartId = data.chartId || 'clothing';
      return (
        this.charts[chartId] ||
        this.charts.clothing ||
        { chart: {}, guideHtml: '' }
      );
    }

    applyPayload(payload, resetUnit) {
      const data = payload || {};
      const entry = this.resolveChartEntry(data);
      this.data = entry.chart || {};

      if (this.titleEl) {
        this.titleEl.textContent = data.title || '';
      }

      if (this.metaEl) {
        const meta = buildMeta(data.inseam, data.fit);
        this.metaEl.innerHTML = meta;
        if (meta) this.metaEl.removeAttribute('hidden');
        else this.metaEl.setAttribute('hidden', '');
      }

      if (this.howtoEl) {
        const html = entry.guideHtml || data.guideHtml || '';
        this.howtoEl.innerHTML = html;
        if (html) this.howtoEl.removeAttribute('hidden');
        else this.howtoEl.setAttribute('hidden', '');
      }

      if (resetUnit) {
        this.unit = 'in';
        this.unitButtons.forEach((b) => {
          b.classList.toggle('is-active', b.getAttribute('data-size-guide-unit') === 'in');
        });
      }

      this.populateCountries();
      this.render();
    }

    populateCountries() {
      if (!this.countrySelect) return;
      const countries = this.data.countries || [];
      this.countrySelect.innerHTML = countries
        .map((c) => '<option value="' + c.id + '">' + c.label + '</option>')
        .join('');
      this.countryId = countries[0] ? countries[0].id : '';
    }

    selectedCountry() {
      return (this.data.countries || []).find((c) => c.id === this.countryId) || (this.data.countries || [])[0];
    }

    render() {
      if (!this.head || !this.body) return;

      const country = this.selectedCountry();
      const sizeCol = this.data.sizeColumn || { key: 'alpha', label: 'Size' };
      const measureCols = this.data.measurementColumns || [];
      const unitLabel = this.unit === 'cm' ? 'CM' : 'IN';

      let head = '<tr><th>' + sizeCol.label + '</th>';
      if (country) head += '<th>' + country.columnLabel + '</th>';
      measureCols.forEach((col) => {
        head += '<th>' + col.label + ' (' + unitLabel + ')</th>';
      });
      head += '</tr>';
      this.head.innerHTML = head;

      this.body.innerHTML = (this.data.rows || [])
        .map((row) => {
          let html = '<tr><td>' + (row[sizeCol.key] || '') + '</td>';
          if (country) html += '<td>' + (row[country.columnKey] || '') + '</td>';
          measureCols.forEach((col) => {
            const range = row.measurements && row.measurements[col.key];
            html += '<td>' + formatRange(range, this.unit) + '</td>';
          });
          return html + '</tr>';
        })
        .join('');
    }

    open() {
      setTimeout(() => {
        this.classList.add('active');
        this.setAttribute('aria-hidden', 'false');
      });
      document.body.classList.add('overflow-hidden');
    }

    close() {
      this.classList.remove('active');
      this.setAttribute('aria-hidden', 'true');
      // Keep page locked if cart (or another host drawer) is still open underneath.
      const cartStillOpen = document.querySelector('cart-drawer.active');
      if (!cartStillOpen) {
        document.body.classList.remove('overflow-hidden');
      }
    }
  }

  if (!customElements.get('size-guide-drawer')) {
    customElements.define('size-guide-drawer', SizeGuideDrawer);
  }
})();
